import { Component, computed, effect, inject, input, OnDestroy, output, signal } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { assignToMeAction } from '../../accounts/assign-to-me/store/assign-to-me.actions';
import { selectCurrentUserId } from '../../current-user/store/current-user.selectors';
import { selectAssignToMeUpdated, selectAssignToMeUpdating } from '../../accounts/assign-to-me/store/assign-to-me.selectors';
import { filter, take, takeUntil } from 'rxjs/operators';
import { AccountPointSchema } from '../../models/account';
import { SharedModule } from '../../shared/shared.module';
import { AssignToMeModule } from '../../accounts/assign-to-me/assign-to-me.module';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { AccountUsersV2Component } from '../../shared/account-v2-users/account-v2-users.component';
import { SlaDetailsComponent } from '../../shared/sla-details/sla-details.component';
import { SlaRange } from '../../models/sla';
import { MapInfoWindowWrapperComponent } from '../../shared/map-info-window-wrapper/map-info-window-wrapper.component';
import { SummaryItemComponent } from '../../shared/summary-item/summary-item.component';
import { getAllDealStatsAction } from '../../deals/store/get-all-deal-stats/get-all-deal-stats.actions';
import {
  selectGetAllDealStatsByStageStatus,
  selectGetAllDealStatsLoading
} from '../../deals/store/get-all-deal-stats/get-all-deal-stats.selectors';
import { GetAllDealStatsModule } from '../../deals/store/get-all-deal-stats/get-all-deal-stats.module';
import { Subject } from 'rxjs';
import { GetDealsFilterGroupsSchema } from '../../models/deal';
import { FormatAmountPipe } from '../../shared/pipes/format-amount.pipe';
import { CdkMenu, CdkMenuItem, CdkMenuTrigger } from '@angular/cdk/menu';

@Component({
  selector: 'app-location-info-window-content',
  templateUrl: './location-info-window-content.component.html',
  styleUrls: [ './location-info-window-content.component.scss' ],
  imports: [
    SharedModule,
    AssignToMeModule,
    NgbTooltip,
    AccountUsersV2Component,
    SlaDetailsComponent,
    MapInfoWindowWrapperComponent,
    SummaryItemComponent,
    GetAllDealStatsModule,
    FormatAmountPipe,
    CdkMenuTrigger,
    CdkMenu,
    CdkMenuItem,
  ]
})
export class LocationInfoWindowContentComponent implements OnDestroy {
  private readonly store = inject(Store);

  assignedToMe = output<void>();
  closeEvent = output<void>();

  account = input.required<AccountPointSchema>();
  slaRange = input.required<SlaRange>();
  groupAccounts = input<AccountPointSchema[]>();
  dealsFilterGroups = input<GetDealsFilterGroupsSchema>();

  selected = signal(0);
  selectedAccount = computed(() =>
    (this.groupAccounts() || [])[this.selected()]
  );

  assignToMeUpdating = this.store.selectSignal(selectAssignToMeUpdating);
  currentUserId = this.store.selectSignal(selectCurrentUserId);

  isNotBelongsToCurrentUser = computed(() =>
    !(this.selectedAccount() || this.account()).users.some(u => u.id === this.currentUserId())
  );

  dealOpenStats = this.store.selectSignal(selectGetAllDealStatsByStageStatus('open'));
  dealWonStats = this.store.selectSignal(selectGetAllDealStatsByStageStatus('won'));
  dealLostStats = this.store.selectSignal(selectGetAllDealStatsByStageStatus('lost'));
  dealStatsLoading = this.store.selectSignal(selectGetAllDealStatsLoading);

  private readonly unsubscribe$ = new Subject<void>();

  resetSelectedEffect = effect(() => {
    if (this.groupAccounts()) {
      this.selected.set(0);
    }
  }, { allowSignalWrites: true });

  fetchDealStatsEffect = effect(() => {
    const dealsFilterGroups = this.dealsFilterGroups();
    this.store.dispatch(getAllDealStatsAction({
      payload: {
        filters: { accounts: [ this.selectedAccount()?.fbId || this.account().fbId ] },
        ...(dealsFilterGroups ? {
          filterGroups: dealsFilterGroups,
        } : {}),
      },
    }));
  }, { allowSignalWrites: true });

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  assignToMe(): void {
    this.store.dispatch(assignToMeAction({
      payload: {
        accountId: this.account().fbId,
      },
    }));

    this.store.pipe(
      select(selectAssignToMeUpdated),
      filter(updated => updated),
      take(1),
      takeUntil(this.unsubscribe$)
    ).subscribe(() => this.assignedToMe.emit());
  }

  onNext(): void {
    this.selected.update(n => n === (this.groupAccounts() || []).length - 1 ? 0 : n + 1);
  }
}
